/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { InlineNotification } from '@coursera/cds-core';

import { getFileSizeBytesFromUrl } from 'bundles/assess-common/utils/fileUploadUtils';
import {
  aiGradingFileTypesAudio,
  aiGradingFileTypesVideo,
  allowedFileTypesForAiGrading,
  mediaMaxBytesForAiPeerReview,
} from 'bundles/grading-common/utils/constants';
import { useAiGradingContext } from 'bundles/peer/components/ai-grading/AiEligibilityProvider';

import _t from 'i18n!nls/grading-common';

type Props = {
  fileUrl?: string;
};

const styles = {
  marginBottom: () => css`
    margin-top: var(--cds-spacing-150);
  `,
};

const UnsupportedFileForAiGradingNotification = ({ fileUrl }: Props) => {
  const [fileSize, setFileSize] = useState<number | null>(null);
  const { isAiGradingFlowPreview, updateUnsupportedFileForAiGradingInSubmission } = useAiGradingContext();
  const isAiGradingFlow = isAiGradingFlowPreview();
  const fileExtension = fileUrl?.split('.').pop()?.toLowerCase() || '';
  const isSupportedFileType = Object.values(allowedFileTypesForAiGrading).includes(fileExtension);

  let maxFileSize = 0;
  if (
    Object.values(aiGradingFileTypesAudio).includes(fileExtension) ||
    Object.values(aiGradingFileTypesVideo).includes(fileExtension)
  ) {
    maxFileSize = mediaMaxBytesForAiPeerReview;
  }

  useEffect(() => {
    const fetchFileSizeBytes = async () => {
      if (fileUrl) {
        const fileSizeBytes = await getFileSizeBytesFromUrl(fileUrl);
        setFileSize(fileSizeBytes);
      }
    };
    if (isAiGradingFlow) {
      fetchFileSizeBytes();
    }
  }, [isAiGradingFlow, fileUrl]);

  useEffect(() => {
    // keep a log of which file is supported and which is not
    // since a single submission can have multiple file uploads, it will be easier to track in context
    // to compare against during submission
    if (isAiGradingFlow && fileUrl) {
      if (!isSupportedFileType || (fileSize !== null && fileSize > maxFileSize && maxFileSize > 0)) {
        updateUnsupportedFileForAiGradingInSubmission(fileUrl, true);
      } else {
        updateUnsupportedFileForAiGradingInSubmission(fileUrl, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAiGradingFlow, fileUrl, fileSize, isSupportedFileType, maxFileSize]);

  if (isAiGradingFlow && fileUrl && !isSupportedFileType) {
    return (
      <InlineNotification
        severity="warning"
        css={styles.marginBottom}
        title={_t("This file type can't be graded by AI")}
      >
        {_t(
          "Your assignment will be graded by your peers because your file type isn't supported by AI grading. If you would like to switch to AI grading, upload another file."
        )}
      </InlineNotification>
    );
  }

  if (isAiGradingFlow && fileUrl && fileSize !== null && fileSize > maxFileSize && maxFileSize > 0) {
    return (
      <InlineNotification severity="warning" css={styles.marginBottom} title={_t('File size too large for AI grading')}>
        {_t(
          'Your assignment will be graded by your peers because your file size exceeds the limit for AI grading. If you would like to switch to AI grading, upload a smaller file.'
        )}
      </InlineNotification>
    );
  }

  return null;
};

export default UnsupportedFileForAiGradingNotification;
